import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Users,
  UserPlus,
  Cake,
  User,
  MessageSquare,
  Mail,
  Heart,
  HandHeart,
  LogOut,
  Phone,
  Search,
  HelpCircle,
  Bell,
  Smartphone,
  RefreshCw
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const HelpPage = () => {
  const navigate = useNavigate();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Function to clear cache and refresh the app
  const handleRefreshApp = async () => {
    setIsRefreshing(true);
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Tell service worker to skip waiting and activate new version
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      }
      
      // Clear localStorage items that might cause stale UI
      localStorage.removeItem('hideWeddingCard');
      
      // Wait a moment then reload
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    } catch (error) {
      console.error('Error refreshing app:', error);
      // Reload anyway
      window.location.reload(true);
    }
  };

  const helpTopics = [
    {
      id: 'enable-notifications',
      icon: Bell,
      title: 'Get Notifications & App Badge',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      importantNote: 'To see the red notification badge on your app icon (like text messages), you MUST add the app to your Home Screen first!',
      steps: [
        'FIRST: Add the app to your Home Screen (see instructions below)',
        'Open the app FROM your Home Screen icon',
        'Tap "Profile" at the bottom of the screen',
        'Tap "Settings" in the menu',
        'Find "Push Notifications" and tap to turn it ON',
        'When your phone asks, tap "Allow"'
      ],
      tip: 'Once enabled, you will see a red number on your app icon when you have new messages, prayer requests, or inspirational posts - even when the app is closed!',
      extraInfo: {
        title: 'For iPhone Users - Add to Home Screen',
        steps: [
          '1. Open the app in Safari browser',
          '2. Tap the Share button (square with arrow at bottom)',
          '3. Scroll down and tap "Add to Home Screen"',
          '4. Tap "Add" in the top right',
          '5. Now open the app FROM your Home Screen',
          '6. Enable notifications in Profile → Settings'
        ]
      },
      androidInfo: {
        title: 'For Android Users - Add to Home Screen',
        text: '1. Open the app in Chrome browser\n2. Tap the menu (3 dots) in the top right\n3. Tap "Add to Home Screen" or "Install App"\n4. Open the app FROM your Home Screen\n5. Enable notifications in Profile → Settings'
      }
    },
    {
      id: 'leaders-approve',
      icon: Users,
      title: 'Leaders: Approving Members',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      steps: [
        'Go to your Group page',
        'Look for the notification badge',
        'Tap "Pending Requests"',
        'You will see members waiting to join',
        'Tap "Approve" to let them in',
        'Or tap "Decline" to deny'
      ],
      tip: 'You will get a notification when someone wants to join your group.'
    },
    {
      id: 'join-group',
      icon: UserPlus,
      title: 'Joining a Group',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      steps: [
        'Tap "Groups" at the bottom',
        'Browse the list of groups',
        'Find a group you want to join',
        'Tap on the group name',
        'Tap "Request to Join"',
        'Wait for the leader to approve you'
      ],
      tip: 'You will get a notification when you are approved!'
    },
    {
      id: 'birthday-wishes',
      icon: Cake,
      title: 'Sending Birthday Wishes',
      color: 'text-pink-600',
      bgColor: 'bg-pink-100',
      steps: [
        'Go to Home',
        'Look for the pink birthday card',
        'Tap the 💬 message icon',
        'Type your birthday message',
        'Tap the pink Send button'
      ],
      tip: 'Your wish will be visible to everyone in the church!'
    },
    {
      id: 'update-profile',
      icon: User,
      title: 'Updating Your Profile',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      steps: [
        'Tap "Profile" at the bottom',
        'Tap the "Edit Profile" button',
        'Update your information',
        'Add your birthday and anniversary',
        'Tap "Save Changes"'
      ],
      tip: 'Adding your birthday lets the church celebrate you!'
    },
    {
      id: 'send-messages',
      icon: MessageSquare,
      title: 'Sending Messages',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      steps: [
        'Tap "Messages" at the bottom',
        'Tap "Inbox" to send a direct message',
        'Tap "New Message" or the + button',
        'Select a member to message',
        'Type your message',
        'Tap Send'
      ],
      tip: 'Direct messages are private between you and that person.'
    },
    {
      id: 'find-messages',
      icon: Mail,
      title: 'Finding & Replying to Messages',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
      steps: [
        'Tap "Messages" at the bottom',
        'Tap "Inbox" to see your messages',
        'Tap on a conversation to open it',
        'Type your reply at the bottom',
        'Tap Send'
      ],
      tip: 'A red badge shows how many unread messages you have.'
    },
    {
      id: 'prayer-request',
      icon: Heart,
      title: 'Posting a Prayer Request',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      steps: [
        'Go to Home',
        'Scroll down to "Prayer Community"',
        'Tap "Add Prayer Request"',
        'Type your prayer request',
        'Choose if you want your name shown',
        'Tap "Submit"'
      ],
      tip: 'You can post anonymously if you prefer privacy.'
    },
    {
      id: 'benevolent',
      icon: HandHeart,
      title: 'Requesting Benevolent Help',
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      steps: [
        'Go to Home',
        'Scroll down to "Benevolent Requests"',
        'Tap "Submit Request"',
        'Describe what help you need',
        'Tap "Submit"'
      ],
      tip: 'Your request goes to church leadership for review.'
    },
    {
      id: 'leave-group',
      icon: LogOut,
      title: 'Leaving a Group',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      steps: [
        'Tap "Groups" at the bottom',
        'Find the group you want to leave',
        'Tap on the group to open it',
        'Tap the menu icon (three dots)',
        'Tap "Leave Group"',
        'Confirm by tapping "Yes"'
      ],
      tip: 'You can always request to rejoin later.'
    },
    {
      id: 'call-member',
      icon: Phone,
      title: 'Calling or Finding a Member',
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
      steps: [
        'Tap "Directory" at the bottom',
        'Type their name in the search box',
        'Tap their name to see their info',
        'Tap the 📞 phone icon to call',
        'Your phone will dial their number'
      ],
      tip: 'You can also tap the ✉️ icon to send them a message.'
    }
  ];

  const handleCall = () => {
    window.location.href = 'tel:5597904492';
  };

  const handleMessage = () => {
    navigate('/messages');
    // Could navigate directly to DM with Anessa: navigate('/dm/user-admin-anessa');
  };

  if (selectedTopic) {
    const topic = helpTopics.find(t => t.id === selectedTopic);
    const TopicIcon = topic.icon;
    
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-lg mx-auto px-4 py-4">
            <button 
              onClick={() => setSelectedTopic(null)}
              className="flex items-center gap-2 text-navy-900 font-medium"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-lg mx-auto px-4 py-6">
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-6">
              {/* Topic Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 ${topic.bgColor} rounded-xl flex items-center justify-center`}>
                  <TopicIcon className={`w-6 h-6 ${topic.color}`} />
                </div>
                <h1 className="text-xl font-bold text-navy-900">{topic.title}</h1>
              </div>

              {/* Important Note (highlighted warning) */}
              {topic.importantNote && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6">
                  <p className="text-red-700 font-bold text-center">
                    ⚠️ {topic.importantNote}
                  </p>
                </div>
              )}

              {/* Steps */}
              <div className="space-y-4 mb-6">
                {topic.steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-navy-900 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-lg text-gray-700 pt-1">{step}</p>
                  </div>
                ))}
              </div>

              {/* Tip */}
              {topic.tip && (
                <div className="bg-tan-100 border border-tan-300 rounded-xl p-4">
                  <p className="text-tan-800 font-medium">
                    ✅ {topic.tip}
                  </p>
                </div>
              )}
              
              {/* Extra Info (for iPhone instructions, etc.) */}
              {topic.extraInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-5 h-5 text-blue-600" />
                    <p className="text-blue-800 font-bold">{topic.extraInfo.title}</p>
                  </div>
                  <div className="space-y-2">
                    {topic.extraInfo.steps.map((step, index) => (
                      <p key={index} className="text-blue-700 text-sm">{step}</p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Android Info */}
              {topic.androidInfo && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 font-bold">{topic.androidInfo.title}</p>
                  </div>
                  <p className="text-green-700 text-sm whitespace-pre-line">{topic.androidInfo.text}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Need Help Card */}
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mt-6 bg-gradient-to-r from-navy-900 to-navy-800">
            <CardContent className="p-6">
              <h3 className="text-white font-bold text-lg mb-2">Need More Help?</h3>
              <p className="text-gray-300 mb-4">
                Contact Sister Anessa Leydig for personalized assistance.
              </p>
              <div className="flex gap-3">
                <Button 
                  onClick={handleCall}
                  className="flex-1 bg-white text-navy-900 hover:bg-gray-100"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
                <Button 
                  onClick={handleMessage}
                  className="flex-1 bg-tan-500 text-white hover:bg-tan-600"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-navy-900 font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Title Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mb-6 bg-gradient-to-r from-tan-500 to-tan-600">
          <CardContent className="p-6 text-center">
            <HelpCircle className="w-12 h-12 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white mb-2">App Help</h1>
            <p className="text-tan-100">
              Tap any topic below for step-by-step instructions
            </p>
          </CardContent>
        </Card>

        {/* Help Topics */}
        <div className="space-y-3">
          {helpTopics.map((topic) => {
            const TopicIcon = topic.icon;
            return (
              <Card 
                key={topic.id}
                className="border-0 shadow-md rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setSelectedTopic(topic.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${topic.bgColor} rounded-lg flex items-center justify-center`}>
                        <TopicIcon className={`w-5 h-5 ${topic.color}`} />
                      </div>
                      <span className="font-medium text-navy-900 text-lg">{topic.title}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Need Help Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mt-6 bg-gradient-to-r from-navy-900 to-navy-800">
          <CardContent className="p-6">
            <h3 className="text-white font-bold text-lg mb-2">Need Personalized Assistance?</h3>
            <p className="text-gray-300 mb-4">
              Contact Sister Anessa Leydig for help with the app.
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={handleCall}
                className="flex-1 bg-white text-navy-900 hover:bg-gray-100"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call
              </Button>
              <Button 
                onClick={handleMessage}
                className="flex-1 bg-tan-500 text-white hover:bg-tan-600"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Refresh App Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mt-4 bg-gradient-to-r from-blue-600 to-blue-500">
          <CardContent className="p-6">
            <h3 className="text-white font-bold text-lg mb-2">App Not Working Right?</h3>
            <p className="text-blue-100 mb-4 text-sm">
              If you're seeing old content or the app isn't working properly, tap the button below to refresh and get the latest version.
            </p>
            <Button 
              onClick={handleRefreshApp}
              disabled={isRefreshing}
              className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold"
              data-testid="refresh-app-btn"
            >
              {isRefreshing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh App
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      <BottomNav />
    </div>
  );
};

export default HelpPage;
